import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
 
import {API_URL, API_VERSION} from '../../config/config';
const SERVER_API = API_URL;
const api = axios.create({
  baseURL: `${SERVER_API}/${API_VERSION}`,
});
const refreshApi = axios.create({
  baseURL: `${SERVER_API}/${API_VERSION}`,
});

interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number; // Token expiration time in seconds
}

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const notifyRefreshSubscribers = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

api.interceptors.request.use(
    async (
      config: InternalAxiosRequestConfig,
    ): Promise<InternalAxiosRequestConfig> => {
      if (config.headers?.['Require-Token'] && !config.headers['Custom-Token']) {
        const currentToken = await getToken();
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        delete config.headers['Require-Token'];
      } else if (config.headers['Custom-Token']) {
        config.headers.Authorization = `Bearer ${config.headers.CustomToken}`;
      }
      return config;
    },
    (error: AxiosError): Promise<AxiosError> => {
      return Promise.reject(error);
    },
  );

api.interceptors.request.use(async config => {
  const token = await localStorage.getItem('authTokenObj');
  if (token) {
    const { access_token } = JSON.parse(token);
    config.headers.Authorization = `Bearer ${access_token}`;
  }
  return config;
});

const getToken = async (): Promise<string | null> => {
  try {
    const token = localStorage.getItem('authTokenObj');
    if (!token) return null;
    const tokenObj: TokenResponse = JSON.parse(token);
    return tokenObj.access_token;
  } catch (error) {
    console.log('Error getting token from AsyncStorage:', error);
    return null;
  }
};

const initializeAuthHeader = async () => {
  const token = await getToken();
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
};

initializeAuthHeader();

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;
    const requestUrl = originalRequest?.url || '';
    const isRefreshRequest = requestUrl.includes('/auth/refresh-token') || requestUrl.includes('/auth/refresh');

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshRequest
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise(resolve => {
          addRefreshSubscriber((newToken: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken?.access_token) {
          notifyRefreshSubscribers(refreshedToken.access_token);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${refreshedToken.access_token}`;
          }
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.clear();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export const refreshAccessToken = async (): Promise<TokenResponse | null> => {
  const refreshToken = localStorage.getItem('authTokenObj'); // Replace with secure storage in production
  if (!refreshToken) throw new Error('No refresh token available.');
  const tokenObj = JSON.parse(refreshToken);
  console.log('Refreshing token with:', tokenObj);
  // const payload = new URLSearchParams();
  // payload.append('refresh_token', tokenObj.refresh_token);
  const {data} = await refreshApi.post(
    '/auth/refresh',
    {
      refresh_token: tokenObj.refresh_token, // ✅ JSON payload
    },
    {
      headers: {
        'Content-Type': 'application/json', // ✅ Correct content type
      },
    }
  );
  const newToken: TokenResponse = data;
  console.log('Received new token:', newToken);
  localStorage.setItem('authTokenObj', JSON.stringify(newToken));
  return newToken;
};

export const apiClient = {
  get: (url: string, config?: any) =>
    api.get(url, config).then(res => res.data),
  post: (url: string, data: any, config?: any) =>
    api.post(url, data, config).then(res => res.data),
  patch: (url: string, data: any, config?: any) =>
    api.patch(url, data, config).then(res => res.data),
  put: (url: string, data: any, config?: any) =>
    api.put(url, data, config).then(res => res.data),
  delete: (url: string, config?: any) =>
    api.delete(url, config).then(res => res.data),
};
export default api;
